import { IUsersConfiguration } from 'src/types/configuration/IUsersConfiguration';
import { ConfigurationItem } from './types';

/**
 * Transforms a raw `ConfigurationItem` from the API into a flat configurator
 * configuration object, applying the avatar legacy-field adapter on the `chat`
 * slice to normalise deprecated `hide_*_avatar` boolean flags into the current
 * `*_logo: false` representation.
 *
 * @param item - Raw item returned by the configuration API.
 * @returns Flat configuration object with `id`, `name`, and normalised fields.
 */
export const configurationItemAdapter = ({
	data,
	subconf,
}: ConfigurationItem) => ({
	...data,
	chat: data.chat ? adaptUsersConfiguration(data.chat) : data.chat,
	id: subconf,
	name: subconf,
});

/**
 * Normalizes a raw avatar configuration object from the API, mapping
 * deprecated `hide_*_avatar` boolean flags to the canonical `*_logo: false`
 * representation and stripping the deprecated keys.
 *
 * This adapter should be applied at every API boundary (configurator fetch /
 * lib `fetchConfiguration`) so that all downstream code can rely solely on
 * the `*_logo` value to determine avatar visibility.
 *
 * Migration rules:
 * - `hide_*_avatar: true`  → `*_logo: false`  (avatar hidden)
 * - `hide_*_avatar: false` → `*_logo` unchanged (no-op)
 * - `hide_*_avatar` absent → `*_logo` unchanged
 *
 * When both `hide_*_avatar: true` and a `*_logo` string are present in a
 * legacy payload, the hide flag wins (avatar is hidden, string is dropped).
 *
 * @param config - Raw `IUsersConfiguration` that may contain deprecated fields.
 * @returns A new object with deprecated fields removed and `*_logo` normalised.
 */
export const adaptUsersConfiguration = (
	config: IUsersConfiguration,
): IUsersConfiguration => {
	const {
		hide_user_avatar,
		hide_agent_avatar,
		hide_system_avatar,
		user_logo,
		agent_logo,
		system_logo,
		...rest
	} = config;

	return {
		...rest,
		user_logo: hide_user_avatar ? false : user_logo,
		agent_logo: hide_agent_avatar ? false : agent_logo,
		system_logo: hide_system_avatar ? false : system_logo,
	};
};
